import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaLinkedin, FaTwitter, FaGithub, FaEnvelope, FaPhone } from 'react-icons/fa';
import NewsletterSubscription from './NewsletterSubscription';

const FooterContainer = styled.footer`
  background: var(--secondary-900);
  color: var(--secondary-200);
  padding: var(--space-3xl) 0 var(--space-lg);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-2xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-xl);
  }
`;

const FooterSection = styled.div`
  h3 {
    color: white;
    font-size: 1.125rem;
    margin-bottom: var(--space-lg);
  }

  p {
    line-height: 1.6;
    margin-bottom: var(--space-md);
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);

  a {
    color: var(--secondary-300);
    text-decoration: none;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--primary-400);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-md);

  a {
    color: var(--secondary-400);
    font-size: 1.25rem;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--primary-400);
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);

  div {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    color: var(--secondary-300);

    svg {
      color: var(--primary-400);
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: var(--space-2xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--secondary-700);
  text-align: center;
  color: var(--secondary-400);
`;

const Footer = () => {
  const { t } = useTranslation();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>ScrumNX</h3>
          <p>{t('footer.tagline')}</p>
          <SocialLinks aria-label={t('accessibility.socialMediaLinks')}>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label={t('accessibility.visitLinkedIn')}>
              <FaLinkedin />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label={t('accessibility.visitTwitter')}>
              <FaTwitter />
            </a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" aria-label={t('accessibility.visitGitHub')}>
              <FaGithub />
            </a>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <h3>{t('footer.quickLinks')}</h3>
          <FooterLinks role="list">
            <Link to="/" onClick={scrollToTop} role="listitem">{t('common.home')}</Link>
            <Link to="/services" onClick={scrollToTop} role="listitem">{t('common.services')}</Link>
            <Link to="/blog" onClick={scrollToTop} role="listitem">{t('common.blog')}</Link>
            <Link to="/contact" onClick={scrollToTop} role="listitem">{t('common.contact')}</Link>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <h3>{t('footer.services')}</h3>
          <FooterLinks role="list">
            <Link to="/services" onClick={scrollToTop} role="listitem">{t('footer.agileTransformation')}</Link>
            <Link to="/services" onClick={scrollToTop} role="listitem">{t('footer.scrumTraining')}</Link>
            <Link to="/services" onClick={scrollToTop} role="listitem">{t('footer.teamCoaching')}</Link>
            <Link to="/services" onClick={scrollToTop} role="listitem">{t('footer.digitalTransformation')}</Link>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <h3>{t('footer.contactInfo')}</h3>
          <ContactInfo>
            <div>
              <FaEnvelope aria-hidden="true" />
              <span>contact@scrumnx.com</span>
            </div>
            <div>
              <FaPhone aria-hidden="true" />
              <span>+49 17630574191</span>
            </div>
          </ContactInfo>
        </FooterSection>

        <FooterSection style={{ gridColumn: '1 / -1' }}>
          <NewsletterSubscription />
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-lg)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
          <Link to="/privacy-policy" onClick={scrollToTop} style={{ color: 'var(--secondary-400)', textDecoration: 'none', fontSize: '0.875rem' }}>
            {t('footer.privacyPolicy')}
          </Link>
          <Link to="/terms-of-service" onClick={scrollToTop} style={{ color: 'var(--secondary-400)', textDecoration: 'none', fontSize: '0.875rem' }}>
            {t('footer.termsOfService')}
          </Link>
          <Link to="/imprint" onClick={scrollToTop} style={{ color: 'var(--secondary-400)', textDecoration: 'none', fontSize: '0.875rem' }}>
            {t('footer.imprint')}
          </Link>
        </div>
        <p>&copy; 2026 ScrumNX. {t('footer.allRightsReserved')}.</p>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;